import React, { useState, useEffect } from 'react';
import { Destination, DestinationPageResponse } from '../../types';
import { apiService } from '../../services/api';
import { DestinationList } from './DestinationList';
import { CreateDestinationForm } from './CreateDestinationForm';
import { UpdateDestinationForm } from './UpdateDestinationForm';
import { ViewDestinationPage } from './ViewDestinationPage';
import { EditDestinationImagesPage } from './EditDestinationImagesPage';
import { ConfirmDialog } from '../common/ConfirmDialog';
import toast from 'react-hot-toast';

type ViewMode = 'list' | 'create' | 'edit' | 'view' | 'edit-images';

export const DestinationsPage: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [selectedDestination, setSelectedDestination] = useState<Destination | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const pageSize = 10;

  // Load destinations
  const loadDestinations = async (page: number = 0, query?: string) => {
    try {
      setLoading(true);
      setError(null);
      const data: DestinationPageResponse = await apiService.getDestinations(page, pageSize, query);
      setDestinations(data.content || []);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      setCurrentPage(data.number || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to load destinations');
      toast.error(err.message || 'Failed to load destinations');
      // Ensure destinations is always an array even on error
      setDestinations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDestinations(0, searchQuery);
  }, []);

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    loadDestinations(0, query);
  };

  const handlePageChange = (page: number) => {
    loadDestinations(page, searchQuery);
  };

  const handleCreateDestination = async (data: any) => {
    try {
      setLoading(true);
      await apiService.createDestination(data);
      toast.success('Destination created successfully');
      await loadDestinations(currentPage, searchQuery);
      setViewMode('list');
    } catch (err: any) {
      toast.error(err.message || 'Failed to create destination');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDestination = async (id: string, data: any) => {
    try {
      setLoading(true);
      await apiService.updateDestination(id, data);
      toast.success('Destination updated successfully');
      await loadDestinations(currentPage, searchQuery);
      setViewMode('list');
      setSelectedDestination(null);
    } catch (err: any) {
      toast.error(err.message || 'Failed to update destination');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const [destinationToDelete, setDestinationToDelete] = useState<string | null>(null);

  const handleDeleteDestination = (id: string) => {
    setDestinationToDelete(id);
  };

  const confirmDelete = async () => {
    if (!destinationToDelete) return;
    const id = destinationToDelete;
    setDestinationToDelete(null);

    try {
      setLoading(true);
      await apiService.deleteDestination(id);
      toast.success('Destination deleted successfully');
      await loadDestinations(currentPage, searchQuery);
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete destination');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (destination: Destination) => {
    setSelectedDestination(destination);
    setViewMode('edit');
  };

  const handleView = (destination: Destination) => {
    setSelectedDestination(destination);
    setViewMode('view');
  };

  const handleEditImages = (destination: Destination) => {
    setSelectedDestination(destination);
    setViewMode('edit-images');
  };

  const handleCancel = () => {
    setViewMode('list');
    setSelectedDestination(null);
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Manage Destinations
          </h1>
          {viewMode === 'list' && (
            <button
              onClick={() => setViewMode('create')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Create Destination</span>
            </button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <div className="flex items-center justify-between">
              <p className="text-red-800 dark:text-red-200">{error}</p>
              <button
                onClick={() => { setError(null); loadDestinations(); }}
                className="ml-4 text-sm font-medium text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}
      </div>

      {viewMode === 'list' && (
        <DestinationList
          destinations={destinations}
          loading={loading}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          onEdit={handleEdit}
          onView={handleView}
          onEditImages={handleEditImages}
          onDelete={handleDeleteDestination}
          currentPage={currentPage}
          totalPages={totalPages}
          totalElements={totalElements}
          onPageChange={handlePageChange}
        />
      )}

      {viewMode === 'create' && (
        <CreateDestinationForm
          onSubmit={handleCreateDestination}
          onCancel={handleCancel}
          loading={loading}
        />
      )}

      {viewMode === 'view' && selectedDestination && (
        <ViewDestinationPage
          destination={selectedDestination}
          onEdit={() => handleEdit(selectedDestination)}
          onBack={handleCancel}
        />
      )}

      {viewMode === 'edit' && selectedDestination && (
        <UpdateDestinationForm
          destination={selectedDestination}
          onSubmit={(data) => handleUpdateDestination(selectedDestination.id, data)}
          onCancel={handleCancel}
          loading={loading}
        />
      )}

      {viewMode === 'edit-images' && selectedDestination && (
        <EditDestinationImagesPage
          destination={selectedDestination}
          onBack={handleCancel}
          onSave={async (updates) => {
            await handleUpdateDestination(selectedDestination.id, updates);
            setViewMode('list');
          }}
          loading={loading}
        />
      )}
      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!destinationToDelete}
        onConfirm={confirmDelete}
        onCancel={() => setDestinationToDelete(null)}
        title="Delete Destination"
        message="Are you sure you want to delete this destination? This action cannot be undone."
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

